import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, User } from 'lucide-react';
import communityService, { ProfilePreview } from '../../services/communityService';
import DynamicSlider from '../slider/DynamicSlider';
import { t } from '../../i18n';

/** Slider-Location (Web + App an derselben Stelle, Marcel 2026-09-15) */
export const NEW_PLAYERS_SLIDER_LOCATION = 'home_new_players';

// ===================================================================
// „Neue Spieler" — kleine horizontale Avatar-Liste der neuesten
// Community-Profile (nur Profile mit Bild), App-Parität 2026-08-27.
// Blendet sich ohne Daten komplett aus.
// ===================================================================

const ACCENT = '#3fced1';

const HomeNewProfiles: React.FC = () => {
  const [profiles, setProfiles] = useState<ProfilePreview[]>([]);
  const [loaded, setLoaded] = useState(false);
  // 2026-09-15: DynamicSlider „home_new_players“ – Desktop rechts neben den
  // Avataren (Sektion wird kürzer), Mobile darunter. Ohne Slides bleibt alles wie vorher.
  const [sliderCount, setSliderCount] = useState<number | null>(null);
  const sliderPending = sliderCount === null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 'activity' = neue UND zuletzt aktualisierte Profile (Marcel 2026-08-31)
        const res = await communityService.getRecentProfiles({ limit: 12, sort: 'activity' });
        if (cancelled) return;
        setProfiles((res.profiles ?? []).filter((p) => p.image && p.image.length > 0));
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // CLS-Fix (2026-09-01): während des Ladens einen Skeleton in Endhöhe
  // rendern (Heading + Avatar-Reihe) — Profile sind der Normalfall; erst
  // wenn feststeht, dass es keine gibt, kollabiert die Sektion auf null.
  if (!loaded) {
    return (
      <section aria-label={t('web.component.home-new-profiles.neue-spieler')} className="mt-2 mb-8" aria-hidden="true">
        <div className="mb-3">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.home-new-profiles.neue-spieler')}</h2>
          <p className="mt-0.5 text-xs text-zinc-500">{t('web.component.home-new-profiles.neu-zuletzt-aktiv-in-der')}</p>
        </div>
        <div className="flex gap-4 overflow-hidden pb-1">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="w-[72px] shrink-0 text-center">
              <div className="mx-auto h-[68px] w-[68px] animate-pulse rounded-full border-2 border-zinc-800 bg-zinc-800" />
              {/* gleiche Zeilenhöhe wie der Username (text-[11.5px]) */}
              <div className="mt-1.5 text-[11.5px] font-semibold">
                <span className="inline-block h-2.5 w-12 animate-pulse rounded bg-zinc-800 align-middle" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  const slider = (
    <DynamicSlider
      location={NEW_PLAYERS_SLIDER_LOCATION}
      onSlidesChange={setSliderCount}
      className="w-full lg:w-[400px] lg:shrink-0"
    />
  );

  if (profiles.length === 0) {
    // Keine Profile: Slider ggf. allein zeigen (er rendert nichts, wenn leer)
    if (!sliderPending && sliderCount === 0) return null;
    return <div className="mt-2 mb-8 lg:max-w-[400px]">{slider}</div>;
  }

  return (
    // Näher an den Ticker (mt-2), dafür klarer Abstand zu den News (mb-8)
    <section aria-label={t('web.component.home-new-profiles.neue-spieler')} className="mt-2 mb-8">
      {/* Desktop: Überschrift + Avatare links, Slider rechts, vertikal zentriert
          (Marcel 2026-09-15). Mobile: untereinander. Ohne Slides rendert der
          Slider nichts und die linke Spalte nimmt die volle Breite. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
        <div className="min-w-0 flex-1">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.home-new-profiles.neue-spieler')}</h2>
          <p className="mt-0.5 text-xs text-zinc-500">{t('web.component.home-new-profiles.neu-zuletzt-aktiv-in-der')}</p>
        </div>
        <Link
          to="/community"
          className="shrink-0 text-sm font-semibold transition-colors hover:underline"
          style={{ color: ACCENT }}
        >
          {t('web.component.events-filter-bar.alle')}{' '}<ChevronRight size={14} className="inline" />
        </Link>
      </div>

      <div className="flex min-w-0 flex-1 gap-4 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {profiles.map((profile) => {
          const avatarUrl = communityService.buildThumbnailUrl(profile.image, 100);
          return (
            <Link
              key={profile.id}
              to={`/community/profil/${encodeURIComponent(profile.username)}`}
              className="w-[72px] shrink-0 text-center"
            >
              <div className="mx-auto flex h-[68px] w-[68px] items-center justify-center overflow-hidden rounded-full border-2 border-zinc-800 bg-zinc-800 transition hover:border-[#3fced1]/60">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={profile.username}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <User size={28} className="text-zinc-500" />
                )}
              </div>
              <div className="mt-1.5 truncate text-[11.5px] font-semibold text-white">
                {profile.displayName?.trim() || profile.username}
              </div>
            </Link>
          );
        })}
      </div>
        </div>
        {slider}
      </div>
    </section>
  );
};

export default HomeNewProfiles;
